/**
 * PaymentSection
 *
 * Reusable payment entry panel used by IndianBilling and USBilling.
 *
 * Props:
 *   grandTotal        {number}   – total amount due
 *   currency          {string}   – 'INR' | 'USD'
 *   onPaymentsChange  {fn}       – called with current payments array whenever it changes
 *
 * The component is fully self-contained: it fetches global billing settings on mount
 * and exposes the current payment rows upward via onPaymentsChange so the parent can
 * include them in the save payload.
 */

import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { todayCentral } from '../../shared/utils/dateUtils';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const TROY_OZ_IN_GRAMS = 31.1035;

const PAYMENT_TYPES = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Credit Card' },
  { value: 'upi', label: 'UPI', inrOnly: true },
  { value: 'cheque', label: 'Cheque' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'old_gold', label: 'Old Gold' },
  { value: 'gold_coin', label: 'Gold Coin' },
  { value: 'gold_scheme', label: 'Gold Scheme' },
];

// Old Gold and Gold Coin share the same exchange item model/UI; only the
// weight entry unit differs (grams vs troy ounces).
const OLD_GOLD_TYPES = ['old_gold', 'gold_coin'];

const NEEDS_REF = ['cheque', 'bank_transfer', 'upi', 'card'];

// ── Blank row factories ────────────────────────────────────────────────────────
const blankPayment = () => ({
  _key: Math.random().toString(36).slice(2),
  payment_type: 'cash',
  amount: '',
  reference_number: '',
  payment_date: todayCentral(),
  notes: '',
  old_gold_items: [],
  amount_overridden: false,
  enrollment_id: null,
});

const blankOldGoldItem = (defaultMeltingLoss, unit = 'g') => ({
  _key: Math.random().toString(36).slice(2),
  description: '',
  gross_weight_g: '',
  stone_deduction_pct: '0',
  net_gold_weight_g: '',   // derived
  purity_label: '22K',
  purity_factor: '',        // filled from settings or manual
  melting_loss_pct: String(defaultMeltingLoss ?? 3),
  gold_rate_used: '',
  exchange_value: '',       // derived
  unit_entered: unit,       // 'g' (Old Gold) or 'oz' (Gold Coin) — gross/net weight is always stored in grams
});

// ── Helper: recalculate derived fields on an old-gold item ────────────────────
// gross_weight_g is always the gram-equivalent value, converting from ounces first when needed.
function recalcOldGold(item) {
  const enteredWeight = parseFloat(item.gross_weight_g) || 0;
  const gw = item.unit_entered === 'oz' ? enteredWeight * TROY_OZ_IN_GRAMS : enteredWeight;
  const sdp = parseFloat(item.stone_deduction_pct) || 0;
  const ngw = gw * (1 - sdp / 100);
  const pf = parseFloat(item.purity_factor) || 0;
  const ml = parseFloat(item.melting_loss_pct) || 0;
  const gr = parseFloat(item.gold_rate_used) || 0;
  const ev = ngw * pf * (1 - ml / 100) * gr;

  return {
    ...item,
    net_gold_weight_g: ngw > 0 ? ngw.toFixed(3) : '',
    exchange_value: ev > 0 ? ev.toFixed(2) : '',
  };
}

// ── Sub-component: one old-gold item row ──────────────────────────────────────
// The gold rate is always entered in INR per gram, so exchange_value is in INR.
// On USD bills it is converted to dollars for display and for the payment amount.
function OldGoldItemRow({ item, purityOptions, stonePresets, currency, dollarRate, onChange, onRemove }) {
  const fmt = (v, d = 2) => (parseFloat(v) || 0).toFixed(d);
  const isOz = item.unit_entered === 'oz';
  const isUSD = currency === 'USD';
  const valueINR = parseFloat(item.exchange_value) || 0;
  const displayValue = isUSD && dollarRate > 0 ? valueINR / dollarRate : valueINR;

  const update = (patch) => {
    const updated = recalcOldGold({ ...item, ...patch });
    onChange(updated);
  };

  const handlePurityChange = (label) => {
    const pf = purityOptions[label];
    update({ purity_label: label, purity_factor: pf != null ? String(pf) : '' });
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2">
      {/* Row 1 */}
      <div className="flex flex-wrap gap-2 items-end mb-2">
        <div className="flex-1 min-w-[140px]">
          <label className="text-xs text-slate-500 block mb-1">Description</label>
          <input
            type="text"
            placeholder="e.g. Old bangle"
            value={item.description}
            onChange={e => update({ description: e.target.value })}
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
          />
        </div>
        <div className="w-28">
          <label className="text-xs text-slate-500 block mb-1">Gross Wt ({isOz ? 'oz' : 'g'}) *</label>
          <input
            type="number" min="0" step="0.001"
            placeholder="0.000"
            value={item.gross_weight_g}
            onChange={e => update({ gross_weight_g: e.target.value })}
            onFocus={e => e.target.select()}
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
          />
          {isOz && parseFloat(item.gross_weight_g) > 0 && (
            <p className="text-[10px] text-amber-600 mt-0.5">
              = {(parseFloat(item.gross_weight_g) * TROY_OZ_IN_GRAMS).toFixed(3)} g
            </p>
          )}
        </div>
        <div className="w-28">
          <label className="text-xs text-slate-500 block mb-1">Stone Ded. %</label>
          <div className="flex gap-1">
            <select
              value=""
              onChange={e => e.target.value !== '' && update({ stone_deduction_pct: e.target.value })}
              className="text-xs border border-slate-200 rounded px-1 py-1.5 bg-white focus:outline-none flex-shrink-0"
              title="Quick-select preset"
            >
              <option value="">Preset</option>
              {stonePresets.map(p => (
                <option key={p.label} value={p.pct}>{p.label} ({p.pct}%)</option>
              ))}
            </select>
            <input
              type="number" min="0" max="99" step="0.5"
              value={item.stone_deduction_pct}
              onChange={e => update({ stone_deduction_pct: e.target.value })}
              className="w-14 text-sm border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
            />
          </div>
        </div>
        <div className="w-24">
          <label className="text-xs text-slate-500 block mb-1">Net Gold (g)</label>
          <input
            readOnly
            value={item.net_gold_weight_g ? fmt(item.net_gold_weight_g, 3) : ''}
            className="w-full text-sm border border-slate-100 rounded px-2 py-1.5 bg-slate-50 text-slate-600"
            placeholder="—"
          />
        </div>
        <button
          onClick={onRemove}
          className="self-end mb-0.5 p-1.5 text-slate-400 hover:text-red-500 rounded"
          title="Remove"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* Row 2 */}
      <div className="flex flex-wrap gap-2 items-end">
        <div className="w-28">
          <label className="text-xs text-slate-500 block mb-1">Purity *</label>
          <select
            value={item.purity_label}
            onChange={e => handlePurityChange(e.target.value)}
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400"
          >
            {Object.keys(purityOptions).map(k => (
              <option key={k} value={k}>{k}</option>
            ))}
            <option value="custom">Custom</option>
          </select>
        </div>
        <div className="w-24">
          <label className="text-xs text-slate-500 block mb-1">
            Purity Factor
            {item.purity_label !== 'custom' && (
              <span className="text-amber-600 ml-1">({item.purity_label})</span>
            )}
          </label>
          <input
            type="number" min="0" max="1" step="0.0001"
            value={item.purity_factor}
            onChange={e => update({ purity_factor: e.target.value })}
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
            placeholder="0.9160"
          />
        </div>
        <div className="w-28">
          <label className="text-xs text-slate-500 block mb-1">Melting Loss %</label>
          <input
            type="number" min="0" max="20" step="0.5"
            value={item.melting_loss_pct}
            onChange={e => update({ melting_loss_pct: e.target.value })}
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
          />
        </div>
        <div className="w-32">
          <label className="text-xs text-slate-500 block mb-1">24K Rate (₹/g) *</label>
          <input
            type="number" min="0" step="0.01"
            value={item.gold_rate_used}
            onChange={e => update({ gold_rate_used: e.target.value })}
            onFocus={e => e.target.select()}
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
            placeholder="Today's rate"
          />
        </div>
        <div className="flex-1 min-w-[100px]">
          <label className="text-xs font-semibold text-amber-700 block mb-1">Exchange Value</label>
          <div className="text-base font-bold text-amber-700 py-1.5">
            {valueINR > 0
              ? `${isUSD ? '$' : '₹'} ${displayValue.toLocaleString(isUSD ? 'en-US' : 'en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : '—'}
          </div>
          {isUSD && valueINR > 0 && dollarRate > 0 && (
            <p className="text-[10px] text-amber-600 -mt-1">
              ₹{valueINR.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} @ ₹{dollarRate}/$
            </p>
          )}
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-1">
        Stones kept by shop — no stone credit given.
        {isOz && ` Weight entered in troy ounces (1 oz = ${TROY_OZ_IN_GRAMS} g); rate applied per gram.`}
      </p>
    </div>
  );
}

// ── Sub-component: Gold Scheme selector ──────────────────────────────────────
function GoldSchemeSection({ customerId, currency, dollarRate, enrollmentId, onChange }) {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!customerId) { setEnrollments([]); return; }
    setLoading(true);
    fetch(`${API_BASE}/schemes/enrollments/customer/${customerId}`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          const active = (data.data || []).filter(e => e.status === 'active' || e.status === 'completed');
          setEnrollments(active);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [customerId]);

  const toDisplayCurrency = (inrAmount) => {
    if (currency === 'USD' && dollarRate > 0) return (inrAmount / dollarRate).toFixed(2);
    return inrAmount.toFixed(2);
  };

  const currSym = currency === 'USD' ? '$' : '₹';

  const handleSelect = (e) => {
    const id = e.target.value ? parseInt(e.target.value) : null;
    const enrollment = enrollments.find(en => en.id === id);
    if (enrollment) {
      const available = parseFloat(enrollment.paid_amount || 0) - parseFloat(enrollment.redeemed_amount || 0);
      onChange({ enrollmentId: id, amount: toDisplayCurrency(Math.max(0, available)) });
    } else {
      onChange({ enrollmentId: null, amount: '' });
    }
  };

  if (!customerId) {
    return <p className="text-xs text-slate-400 mt-2 italic">Select a customer first to see Gold Scheme balance.</p>;
  }

  if (loading) {
    return <div className="flex items-center gap-2 mt-2 text-xs text-slate-500"><RefreshCw size={12} className="animate-spin" /> Loading schemes…</div>;
  }

  if (enrollments.length === 0) {
    return <p className="text-xs text-amber-600 mt-2">No active Gold Scheme enrollments found for this customer.</p>;
  }

  return (
    <div className="mt-2 space-y-1">
      <label className="text-xs text-slate-500 block">Select Enrollment</label>
      <select
        value={enrollmentId || ''}
        onChange={handleSelect}
        className="w-full text-sm border border-amber-200 rounded px-2 py-1.5 bg-amber-50 focus:outline-none focus:ring-1 focus:ring-amber-400"
      >
        <option value="">— Choose scheme —</option>
        {enrollments.map(en => {
          const available = Math.max(0, parseFloat(en.paid_amount || 0) - parseFloat(en.redeemed_amount || 0));
          return (
            <option key={en.id} value={en.id}>
              {en.scheme_name} — Balance: {currSym}{toDisplayCurrency(available)}
              {en.status === 'completed' ? ' (matured)' : ''}
            </option>
          );
        })}
      </select>
    </div>
  );
}

// ── Sub-component: one payment row ────────────────────────────────────────────
function PaymentRow({ payment, currency, dollarRate, customerId, purityOptions, stonePresets, defaultMeltingLoss, defaultGoldRate, onChange, onRemove }) {
  const [showOldGold, setShowOldGold] = useState(OLD_GOLD_TYPES.includes(payment.payment_type));
  const isGoldCoin = payment.payment_type === 'gold_coin';
  const unit = isGoldCoin ? 'oz' : 'g';

  // Filter payment types by currency
  const availableTypes = PAYMENT_TYPES.filter(t =>
    (!t.usdOnly || currency === 'USD') &&
    (!t.inrOnly || currency === 'INR')
  );

  const update = (patch) => onChange({ ...payment, ...patch });

  // exchange_value is always stored in INR; the payment amount must be in the bill's currency.
  const toBillCurrency = (inr) =>
    currency === 'USD' && dollarRate > 0 ? inr / dollarRate : inr;

  const sumExchangeINR = (items) =>
    items.reduce((s, i) => s + (parseFloat(i.exchange_value) || 0), 0);

  const applyItems = (items, fallbackAmount = '') => {
    const totalINR = sumExchangeINR(items);
    // Respect a manually-entered amount; only auto-fill when the user hasn't overridden it.
    if (payment.amount_overridden) {
      return { old_gold_items: items };
    }
    return {
      old_gold_items: items,
      amount: totalINR > 0 ? toBillCurrency(totalINR).toFixed(2) : fallbackAmount,
    };
  };

  const newOldGoldItem = (itemUnit) => {
    const item = blankOldGoldItem(defaultMeltingLoss, itemUnit);
    // Pre-fill today's gold rate if available
    if (defaultGoldRate) item.gold_rate_used = String(defaultGoldRate);
    return item;
  };

  const handleTypeChange = (type) => {
    // Changing the type discards any manual amount override so the new type recalculates cleanly.
    const patch = { payment_type: type, amount_overridden: false };
    if (OLD_GOLD_TYPES.includes(type)) {
      const newUnit = type === 'gold_coin' ? 'oz' : 'g';
      // Seed one blank item, or re-tag existing items with the new unit and
      // recalculate them since the entered weight now means something different.
      const items = payment.old_gold_items.length === 0
        ? [newOldGoldItem(newUnit)]
        : payment.old_gold_items.map(i => recalcOldGold({ ...i, unit_entered: newUnit }));
      const totalINR = sumExchangeINR(items);
      patch.old_gold_items = items;
      patch.amount = totalINR > 0 ? toBillCurrency(totalINR).toFixed(2) : payment.amount;
      setShowOldGold(true);
    }
    update(patch);
  };

  const addOldGoldItem = () => {
    update({ old_gold_items: [...payment.old_gold_items, newOldGoldItem(unit)] });
  };

  const updateOldGoldItem = (key, updated) => {
    const items = payment.old_gold_items.map(i => i._key === key ? updated : i);
    update(applyItems(items, payment.amount));
  };

  const removeOldGoldItem = (key) => {
    const items = payment.old_gold_items.filter(i => i._key !== key);
    update(applyItems(items, ''));
  };

  const oldGoldTotalINR = sumExchangeINR(payment.old_gold_items);
  const oldGoldTotal = toBillCurrency(oldGoldTotalINR);

  return (
    <div className="border border-slate-200 rounded-lg p-3 mb-2 bg-white">
      <div className="flex flex-nowrap gap-2 items-end">
        {/* Payment type */}
        <div className="w-36 flex-shrink-0">
          <label className="text-xs text-slate-500 block mb-1">Payment Type *</label>
          <select
            value={payment.payment_type}
            onChange={e => handleTypeChange(e.target.value)}
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            {availableTypes.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        {/* Amount */}
        <div className="w-28 flex-shrink-0">
          <label className="text-xs text-slate-500 block mb-1 whitespace-nowrap">
            Amount *
            {OLD_GOLD_TYPES.includes(payment.payment_type) && oldGoldTotal > 0 && (
              <span className="text-amber-600 ml-1">
                {payment.amount_overridden ? '(edited)' : '(auto)'}
              </span>
            )}
          </label>
          <input
            type="number" min="0" step="0.01"
            value={payment.amount}
            onChange={e => update(
              OLD_GOLD_TYPES.includes(payment.payment_type)
                ? { amount: e.target.value, amount_overridden: true }
                : { amount: e.target.value }
            )}
            onFocus={e => e.target.select()}
            className={`w-full text-sm border rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 ${OLD_GOLD_TYPES.includes(payment.payment_type) && oldGoldTotal > 0
                ? 'border-amber-200 bg-amber-50 text-amber-700 font-medium'
                : 'border-slate-200'
              }`}
            placeholder="0.00"
          />
          {payment.amount_overridden && oldGoldTotal > 0 && (
            <button
              onClick={() => update({ amount: oldGoldTotal.toFixed(2), amount_overridden: false })}
              className="text-[10px] text-blue-600 hover:text-blue-800 mt-0.5 whitespace-nowrap"
              title={`Calculated exchange value: ${oldGoldTotal.toFixed(2)}`}
            >
              Reset to {currency === 'USD' ? '$' : '₹'}{oldGoldTotal.toFixed(2)}
            </button>
          )}
        </div>

        {/* Reference */}
        {NEEDS_REF.includes(payment.payment_type) && (
          <div className="flex-1 min-w-0">
            <label className="text-xs text-slate-500 block mb-1">Reference No.</label>
            <input
              type="text"
              value={payment.reference_number}
              onChange={e => update({ reference_number: e.target.value })}
              className="w-full text-sm border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
              placeholder="Txn / cheque no."
            />
          </div>
        )}

        {/* Date */}
        <div className="w-32 flex-shrink-0">
          <label className="text-xs text-slate-500 block mb-1">Payment Date</label>
          <input
            type="date"
            value={payment.payment_date}
            onChange={e => update({ payment_date: e.target.value })}
            className="w-full text-sm border border-slate-200 rounded px-1 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </div>

        {/* Notes */}
        <div className="flex-1 min-w-0">
          <label className="text-xs text-slate-500 block mb-1">Notes</label>
          <input
            type="text"
            value={payment.notes}
            onChange={e => update({ notes: e.target.value })}
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
            placeholder="Optional"
          />
        </div>

        {/* Remove button */}
        <button
          onClick={onRemove}
          className="self-end mb-0.5 p-1.5 text-slate-400 hover:text-red-500 rounded flex-shrink-0"
          title="Remove payment row"
        >
          <Trash2 size={15} />
        </button>
      </div>

      {/* Gold Scheme section */}
      {payment.payment_type === 'gold_scheme' && (
        <GoldSchemeSection
          customerId={customerId}
          currency={currency}
          dollarRate={dollarRate}
          enrollmentId={payment.enrollment_id}
          onChange={({ enrollmentId, amount }) => update({ enrollment_id: enrollmentId, amount })}
        />
      )}

      {/* Old Gold / Gold Coin section */}
      {OLD_GOLD_TYPES.includes(payment.payment_type) && (
        <div className="mt-3">
          <button
            onClick={() => setShowOldGold(v => !v)}
            className="flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-900 mb-2"
          >
            {showOldGold ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            {isGoldCoin ? 'Gold Coin' : 'Old Gold'} Details ({payment.old_gold_items.length} item{payment.old_gold_items.length !== 1 ? 's' : ''})
            {oldGoldTotal > 0 && (
              <span className="ml-2 text-amber-700">
                — Exchange Value: {currency === 'USD' ? '$' : '₹'}
                {oldGoldTotal.toLocaleString(currency === 'USD' ? 'en-US' : 'en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            )}
          </button>

          {showOldGold && (
            <div>
              {payment.old_gold_items.map(item => (
                <OldGoldItemRow
                  key={item._key}
                  item={item}
                  purityOptions={purityOptions}
                  stonePresets={stonePresets}
                  currency={currency}
                  dollarRate={dollarRate}
                  onChange={updated => updateOldGoldItem(item._key, updated)}
                  onRemove={() => removeOldGoldItem(item._key)}
                />
              ))}
              <button
                onClick={addOldGoldItem}
                className="flex items-center gap-1.5 text-xs text-amber-700 hover:text-amber-900 px-3 py-1.5 border border-dashed border-amber-300 rounded-lg hover:bg-amber-50 transition-colors"
              >
                <Plus size={12} /> Add another {isGoldCoin ? 'coin' : 'piece'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main PaymentSection ───────────────────────────────────────────────────────
export default function PaymentSection({ grandTotal = 0, currency = 'INR', onPaymentsChange, goldRate = null, customerId = null, dollarRate = null }) {
  // Seed with one Cash row by default so Payment Type is already selected when this opens.
  const [payments, setPayments] = useState(() => [blankPayment()]);
  const [settings, setSettings] = useState(null);
  const [loadingSettings, setLoading] = useState(true);

  // Fetch global billing settings
  useEffect(() => {
    fetch(`${API_BASE}/billing-settings`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (data.success) setSettings(data.data);
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  }, []);

  // Derived settings with fallbacks
  const defaultMeltingLoss = settings?.default_melting_loss_pct ?? 3;
  const purityOptions = settings?.purity_factors ?? { '22K': 0.9160, '18K': 0.7500, '14K': 0.5850, '24K': 1.0, '9K': 0.3750 };
  const stonePresets = settings?.stone_deduction_presets ?? [
    { label: 'None (plain gold)', pct: 0 },
    { label: 'Minimal (1–2 gems)', pct: 5 },
    { label: 'Medium set', pct: 15 },
    { label: 'Heavy stone set', pct: 30 },
    { label: 'Kundan / Jadau', pct: 50 },
  ];

  // Notify parent whenever payments change
  const notify = useCallback((updated) => {
    onPaymentsChange && onPaymentsChange(updated);
  }, [onPaymentsChange]);

  // Push the seeded default Cash row up to the parent on mount.
  useEffect(() => {
    notify(payments);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addPayment = () => {
    const p = blankPayment();
    const updated = [p, ...payments];
    setPayments(updated);
    notify(updated);
  };

  const updatePayment = (key, updated) => {
    const list = payments.map(p => p._key === key ? updated : p);
    setPayments(list);
    notify(list);
  };

  const removePayment = (key) => {
    const list = payments.filter(p => p._key !== key);
    setPayments(list);
    notify(list);
  };

  // Computed totals
  const totalPaid = payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const balance = Math.max(0, grandTotal - totalPaid);
  const isOverpaid = totalPaid > grandTotal + 0.01;
  const isPaid = totalPaid >= grandTotal - 0.01 && totalPaid > 0;

  const fmtINR = (v) => v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const currSym = currency === 'USD' ? '$' : '₹';

  return (
    <div className="mt-6 border-t border-slate-200 pt-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700">Payment</h3>
        <div className="flex items-center gap-4">
          {/* Running totals */}
          {payments.length > 0 && (
            <div className="flex items-center gap-3 text-xs">
              <span className="text-slate-500">
                Paid: <span className="font-semibold text-slate-700">{currSym}{fmtINR(totalPaid)}</span>
              </span>
              {isOverpaid ? (
                <span className="text-red-600 font-semibold">Over by {currSym}{fmtINR(totalPaid - grandTotal)}</span>
              ) : balance > 0 ? (
                <span className="text-orange-600">
                  Balance due: <span className="font-semibold">{currSym}{fmtINR(balance)}</span>
                </span>
              ) : (
                <span className="text-green-600 font-semibold">Fully paid</span>
              )}
            </div>
          )}

          {loadingSettings && <RefreshCw size={12} className="animate-spin text-slate-400" />}

          <button
            onClick={addPayment}
            className="flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 px-3 py-1.5 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
          >
            <Plus size={13} /> Add Payment
          </button>
        </div>
      </div>

      {payments.length === 0 ? (
        <p className="text-xs text-slate-400 italic">
          No payments added yet. Click "Add Payment" to record how the customer is paying.
        </p>
      ) : (
        payments.map(p => (
          <PaymentRow
            key={p._key}
            payment={p}
            currency={currency}
            dollarRate={dollarRate}
            customerId={customerId}
            purityOptions={purityOptions}
            stonePresets={stonePresets}
            defaultMeltingLoss={defaultMeltingLoss}
            defaultGoldRate={goldRate}
            onChange={updated => updatePayment(p._key, updated)}
            onRemove={() => removePayment(p._key)}
          />
        ))
      )}

      {/* Summary bar (shown when at least one payment added) */}
      {payments.length > 0 && (
        <div className={`mt-3 p-3 rounded-lg text-sm flex items-center justify-between ${isPaid ? 'bg-green-50 border border-green-200 text-green-800' :
            isOverpaid ? 'bg-red-50   border border-red-200   text-red-800' :
              'bg-orange-50 border border-orange-200 text-orange-800'
          }`}>
          <span>
            Grand Total: <strong>{currSym}{fmtINR(grandTotal)}</strong>
            &nbsp;·&nbsp;
            Collected: <strong>{currSym}{fmtINR(totalPaid)}</strong>
          </span>
          <span className="font-semibold">
            {isPaid ? 'Fully Paid' :
              isOverpaid ? `Overpaid by ${currSym}${fmtINR(totalPaid - grandTotal)}` :
                `Balance Due: ${currSym}${fmtINR(balance)}`}
          </span>
        </div>
      )}
    </div>
  );
}
